import type { PalworldItem } from "@/interfaces/CardItem";
import { getAaDataFromAPI, getCardsFromAPI } from "@/lib/api/cards-db";
import type { RawAaCard, RawCard } from "@/lib/api/cards-db";
import { cardImageUrl } from "@/lib/cdn";
import { getCardImgproxyUrl } from "@/lib/imgproxy";
import { comparePalworldCardNumbers } from "@/lib/games/palworld/sortCards";

/**
 * Palworld card data, fetched from tcgfan-backend rather than bundled json.
 *
 * Server-side only. The set list is derived from the cards themselves, so
 * adding a set to the scrape makes it appear here with no code change.
 */

export const PALWORLD_GAME_ID = 91;

/**
 * Card art: one full-size webp per card in R2, served through imgproxy so the
 * grid gets a 316x440 encode rather than the ~70KB original, the same as every
 * other game.
 */
export function getPalworldCardImage(
  cardId: string,
  size: string = "316x440",
): string {
  return getCardImgproxyUrl(
    cardImageUrl("palworld", "en", `${cardId}.webp`),
    size,
  );
}

/**
 * Structures are printed sideways: the official site's art for them is
 * landscape, so it is stored landscape too.
 *
 * Components do not ask this directly -- they ask the handler for the card's
 * orientation, which is what makes a sideways card a game's own business rather
 * than a special case in every grid.
 */
export function isPalworldStructure(card: { card_kind?: string }): boolean {
  return card.card_kind === "Structure";
}

function toItem(
  id: string,
  raw: RawCard,
  tcgplayerId: string | undefined,
): PalworldItem {
  return {
    id,
    tcgplayer_id: tcgplayerId,
    name: (raw.name as string) ?? id,
    color: (raw.color as string) ?? "",
    card_number: (raw.card_number as string) ?? id,
    set_id: (raw.set_id as string) ?? "",
    set_name: raw.set_name as string | undefined,
    rarity: (raw.rarity as string) ?? "",
    card_kind: (raw.card_kind as string) ?? "",
    card_kind_sub: raw.card_kind_sub as string | undefined,
    type: (raw.type as string[]) ?? [],
    aptitude: (raw.aptitude as string[]) ?? [],
    cost: raw.cost as number | undefined,
    power: raw.power as number | undefined,
    attack: raw.attack as number | undefined,
    text: raw.text as string | undefined,
    flavor: raw.flavor as string | undefined,
    lucky: raw.lucky as string | undefined,
    timing: raw.timing as string | undefined,
    icon: raw.icon as string | undefined,
    picture: raw.picture as string | undefined,
    sets: (raw.sets as string[]) ?? [],
  };
}

/** Printings per card number, the shape the bundled games' aa_data.json had. */
export type PalworldAaData = Record<string, RawAaCard[]>;

/**
 * Every Palworld printing, keyed by card number.
 *
 * The bundled games import this from their aa_data.json; Palworld has no such
 * file, so it comes from the same table through /api/data/aa-data. Every
 * Palworld card is its own printing -- the rarities are separate card numbers
 * rather than alternate arts -- so each group holds a single `en` row whose id
 * is the card id.
 *
 * Anything that renders cards needs this, not just the TCGplayer mapping: the
 * deck builder draws one tile per printing, so a card with no printings here is
 * a card that never reaches the pool.
 */
export async function getPalworldAaData(): Promise<PalworldAaData> {
  return (await getAaDataFromAPI("palworld")) ?? {};
}

/** Card id -> TCGplayer product id, read off the printings. */
function tcgplayerIdsFrom(aaData: PalworldAaData): Record<string, string> {
  const ids: Record<string, string> = {};

  for (const printings of Object.values(aaData)) {
    for (const printing of printings) {
      if (printing.tcgplayer_id && !ids[printing.id]) {
        ids[printing.id] = printing.tcgplayer_id;
      }
    }
  }

  return ids;
}

export async function getPalworldCards(): Promise<PalworldItem[]> {
  // Both are cached for an hour, so this pair costs one backend round trip
  // each per hour rather than one per request.
  const [cards, aaData] = await Promise.all([
    getCardsFromAPI("palworld"),
    getPalworldAaData(),
  ]);

  if (!cards) return [];

  const tcgplayerIds = tcgplayerIdsFrom(aaData);

  return Object.entries(cards).map(([id, raw]) =>
    toItem(id, raw, tcgplayerIds[id]),
  );
}

export interface PalworldSet {
  /** Set code, e.g. "EBP01" -- the url segment and the product_data set_id. */
  name: string;
  /** Full set name from the official site, e.g. Booster Pack "Dawn of Palpagos". */
  setName: string;
  count: number;
}

/** Sets a card belongs to. A reprint lists every set it was printed in. */
function setsOf(card: PalworldItem): string[] {
  return card.sets?.length ? card.sets : card.set_id ? [card.set_id] : [];
}

/**
 * Every set, newest-looking first: boosters (EBP) before trial decks (ETD)
 * before promos (PR), and within a kind by code descending.
 */
export async function getPalworldSets(): Promise<PalworldSet[]> {
  const cards = await getPalworldCards();
  const sets = new Map<string, PalworldSet>();

  for (const card of cards) {
    // set_name describes the card's own printing, so it only labels the set it
    // was actually printed in. A card reprinted into another set (ESOUL-001 is
    // in both trial decks) must not name that other set.
    const ownSetName =
      card.set_name && card.set_id ? { [card.set_id]: card.set_name } : {};

    for (const setId of setsOf(card)) {
      const existing = sets.get(setId);
      const label = ownSetName[setId];

      if (existing) {
        existing.count += 1;
        // Fill in the real name once a card printed in this set turns up: the
        // first card seen may have been a reprint from elsewhere.
        if (label && existing.setName === setId) {
          existing.setName = label;
        }
      } else {
        sets.set(setId, { name: setId, setName: label ?? setId, count: 1 });
      }
    }
  }

  const kindRank = (name: string) =>
    name.startsWith("EBP") ? 0 : name.startsWith("ETD") ? 1 : 2;

  return [...sets.values()].sort(
    (a, b) =>
      kindRank(a.name) - kindRank(b.name) || b.name.localeCompare(a.name),
  );
}

export async function getPalworldSet(
  setId: string,
): Promise<{ set: PalworldSet | undefined; cards: PalworldItem[] }> {
  const [cards, sets] = await Promise.all([
    getPalworldCards(),
    getPalworldSets(),
  ]);

  const inSet = cards
    .filter((card) => setsOf(card).includes(setId))
    .sort((a, b) => comparePalworldCardNumbers(a.card_number, b.card_number));

  return { set: sets.find((s) => s.name === setId), cards: inSet };
}

export interface PalworldCardPageData {
  card: PalworldItem;
  /** The set the card is being viewed in, for the breadcrumb and neighbours. */
  set: PalworldSet | undefined;
  /** Adjacent cards in that set, so a page can be paged through in order. */
  previous: PalworldItem | undefined;
  next: PalworldItem | undefined;
}

/**
 * One card plus its place in its set. Null when the id does not exist.
 *
 * The neighbours come from the set the card was printed in, in the same order
 * the set page lists them, so stepping through cards from a card page walks the
 * set exactly as the grid reads.
 */
export async function getPalworldCard(
  cardId: string,
): Promise<PalworldCardPageData | null> {
  const cards = await getPalworldCards();
  const card = cards.find((c) => c.id === cardId);

  if (!card) return null;

  // A reprint belongs to several sets; the one it was printed in owns it.
  const setId = card.set_id || setsOf(card)[0];
  const { set, cards: inSet } = await getPalworldSet(setId);
  const index = inSet.findIndex((c) => c.id === cardId);

  return {
    card,
    set,
    previous: index > 0 ? inSet[index - 1] : undefined,
    next: index >= 0 && index < inSet.length - 1 ? inSet[index + 1] : undefined,
  };
}
